<script setup lang="ts">
import { ReviewRequestSchema, UiCard } from '@shopexperts/shared';
import { ref, computed } from 'vue';
import { z } from 'zod';
import { useIntersectionObserver } from '@vueuse/core';
import { useExpertReviewRequests } from '../../composables/reviews/useExpertReviewRequests';
import ReviewRequestCard from '../../components/reviews/ReviewRequestCard.vue';
import SendReminderAside from '../../components/reviews/SendReminderAside.vue';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';

type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

const { reviewRequests, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useExpertReviewRequests();

const sentinelRef = ref<HTMLDivElement | null>(null);
useIntersectionObserver(
  sentinelRef,
  ([entry]) => {
    if (entry && entry.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
      fetchNextPage();
    }
  },
  { threshold: 0.5 }
);

const reminderAsideOpen = ref(false);
const selectedRequestId = ref<number | null>(null);

const selectedRequest = computed<ReviewRequest | null>(() => {
  if (!selectedRequestId.value) return null;
  return reviewRequests.value.find((r) => r.id === selectedRequestId.value) ?? null;
});

const handleSendReminder = (requestId: number) => {
  selectedRequestId.value = requestId;
  reminderAsideOpen.value = true;
};
</script>

<template>
  <div class="space-y-4">
    <div v-if="isLoading" class="space-y-4">
      <UiCard
        v-for="i in 3"
        :key="i"
        padding="md"
        class="animate-pulse"
      >
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div class="flex-1 space-y-3">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </UiCard>
    </div>

    <EmptyDataPlaceholder
      v-else-if="!isLoading && reviewRequests.length === 0"
      title="No Review Requests"
      description="You haven't sent any review requests yet. Click 'Request a Review' to invite your clients to leave feedback."
    />

    <template v-else>
      <ReviewRequestCard
        v-for="request in reviewRequests"
        :key="request.id"
        :request="request"
        @send-reminder="handleSendReminder"
      />

      <div
        ref="sentinelRef"
        class="h-10 flex items-center justify-center"
      >
        <div v-if="isFetchingNextPage" class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
      </div>
    </template>

    <SendReminderAside
      v-model:open="reminderAsideOpen"
      :request="selectedRequest"
    />
  </div>
</template>
